<template>
  <page-loading
    v-if="loading"
    message="Carregando configurações"
    subtitle="Buscando preferências da sua conta"
    :show-preview="false"
  />
  <div v-else class="settings-page pb-16 pb-md-4">
    <v-row class="mt-3">
      <!-- Primeira Coluna: QR Codes (Desktop) -->
      <v-col cols="12" lg="6" :order="1">
        <!-- QR Codes (apenas no desktop) -->
        <v-card elevation="0" class="settings-card pa-4 d-none d-lg-block">
          <v-card-title class="settings-card__title pa-0 mb-4">
            <v-icon left color="primary">mdi-qrcode</v-icon>
            QR Codes
          </v-card-title>
          <v-card-text class="pa-0">
            <p class="settings-card__desc mb-4">
              QR codes para pagamentos e indicações. Podem ser impressos e usados para facilitar transações e
              indicações.
            </p>

            <!-- QR Codes lado a lado -->
            <v-row v-if="staticQRCodeData || indicationQRCodeData" class="mb-6">
              <!-- QR Code de Pagamento Estático -->
              <v-col cols="12" md="6" v-if="staticQRCodeData">
                <v-card elevation="0" class="settings-subcard pa-4 h-100">
                  <v-card-title class="settings-subcard__title pa-0 mb-3">
                    <v-icon left color="primary" small>mdi-credit-card</v-icon>
                    QR Code de Pagamento
                  </v-card-title>
                  <div class="text-center">
                    <img :src="staticQRCodeData" alt="QR Code de Pagamento"
                      class="settings-qr-img" />
                    <p class="caption settings-muted mt-2 mb-3">
                      Pagamentos estáticos
                    </p>
                    <v-btn color="success" dark @click="downloadQRCode('payment')" block>
                      <v-icon left>mdi-download</v-icon>
                      Baixar QR Pagamento
                    </v-btn>
                  </div>
                </v-card>
              </v-col>

              <!-- QR Code de Indicação -->
              <v-col cols="12" md="6" v-if="indicationQRCodeData">
                <v-card elevation="0" class="settings-subcard pa-4 h-100">
                  <v-card-title class="settings-subcard__title pa-0 mb-3">
                    <v-icon left color="primary" small>mdi-account-plus</v-icon>
                    QR Code de Indicação
                  </v-card-title>
                  <div class="text-center">
                    <img :src="indicationQRCodeData" alt="QR Code de Indicação"
                      class="settings-qr-img" />
                    <p class="caption settings-muted mt-2 mb-3">
                      Indicações de parceiros
                    </p>
                    <v-btn color="primary" dark @click="downloadQRCode('indication')" block>
                      <v-icon left>mdi-download</v-icon>
                      Baixar QR Indicação
                    </v-btn>
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Segunda Coluna: Configurações Financeiras (Desktop) / Taxas (Mobile) -->
      <v-col cols="12" lg="6" :order="2">
        <v-card elevation="0" class="settings-card pa-4 mb-4">
          <v-card-title class="settings-card__title pa-0 mb-4">
            <v-icon left color="primary">mdi-currency-usd</v-icon>
            Configurações Financeiras
          </v-card-title>
          <v-card-text class="pa-0">
            <p class="settings-card__desc mb-4">
              Configure taxas e cashback para suas vendas.
            </p>

            <!-- Taxa Administrativa -->
            <div class="mb-4">
              <label class="settings-field-label mb-2 d-flex align-center">
                Taxa administrativa
                <v-tooltip bottom color="primary">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on" small class="ml-2" color="var(--br-placeholder)">
                      mdi-help-circle
                    </v-icon>
                  </template>
                  <span class="subtitle-2">
                    Ao aplicar a taxa de {{ partner.tax }}%, seu cliente
                    receberá 1 BRP para cada
                    <strong>{{ calculatedBRP }}</strong> em compras.
                  </span>
                </v-tooltip>
              </label>
              <v-slider
                min="5"
                max="20"
                :disabled="loading"
                v-model="partner.tax"
                thumb-color="primary"
                track-fill-color="primary"
                track-color="#f5c2cb"
              >
                <template v-slot:append> {{ partner.tax }}% </template>
              </v-slider>
            </div>

            <!-- Cashback Direto -->
            <div class="mb-4">
              <label class="settings-field-label mb-2">Cashback direto
                <v-tooltip bottom color="primary">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on" small class="ml-2" color="var(--br-placeholder)">
                      mdi-help-circle
                    </v-icon>
                  </template>
                  <span class="subtitle-2">O cashback direto é a porcentagem que é imediatamente
                    creditada no saldo do cliente no momento da compra.</span>
                </v-tooltip>
              </label>
              <v-slider
                min="0"
                max="50"
                :disabled="loading"
                v-model="partner.partnerConfigs.instantCashback"
                thumb-color="primary"
                track-fill-color="primary"
                track-color="#f5c2cb"
              >
                <template v-slot:append>
                  {{ partner.partnerConfigs.instantCashback }}%
                </template>
              </v-slider>
            </div>

            <!-- Exemplo de Cálculo -->
            <v-alert type="info" outlined class="mb-0">
              <div class="d-flex align-center">
                <v-icon left>mdi-calculator</v-icon>
                <div>
                  <strong>Exemplo:</strong> Em uma venda de R$ 100,00, você receberá em sua conta
                  <strong>{{ formatPrice(100 - partner.tax - partner.partnerConfigs.instantCashback) }}</strong>
                </div>
              </div>
            </v-alert>
          </v-card-text>
        </v-card>

        <!-- Compra Verde -->
        <v-card v-if="showGreenBuySection" elevation="0" class="settings-card pa-4">
          <v-card-title class="settings-card__title pa-0 mb-4">
            <v-icon left color="success">mdi-leaf</v-icon>
            Compra Verde
          </v-card-title>
          <v-card-text class="pa-0">
            <p class="settings-card__desc mb-4">
              Parceria da BRPoints com a <strong>Elus Gestão Sócio Ambiental</strong> para desenvolvimento sustentável. Ao ativar esta opção,
              você contribui destinando parte do seu lucro para projetos de sustentabilidade.
            </p>

            <v-alert type="success" outlined class="mb-4">
              <div>
                <strong>Impacto Sustentável:</strong>
                <p class="mb-0 mt-1">
                  Ao ativar a Compra Verde, você ajuda a financiar projetos de reflorestamento,
                  energia renovável e preservação ambiental. Juntos, construímos um futuro mais sustentável!
                </p>
              </div>
            </v-alert>

            <v-checkbox
              v-model="partner.partnerConfigs.greenBuy"
              color="success"
              class="mt-0"
            >
              <template v-slot:label>
                <span class="text-subtitle-1">
                  Participar do programa Compra Verde
                </span>
              </template>
            </v-checkbox>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- QR Codes para Mobile (terceira posição) -->
      <v-col cols="12" lg="6" :order="3" class="d-lg-none">
        <v-card elevation="0" class="settings-card pa-4">
          <v-card-title class="settings-card__title pa-0 mb-4">
            <v-icon left color="primary">mdi-qrcode</v-icon>
            QR Codes
          </v-card-title>
          <v-card-text class="pa-0">
            <p class="settings-card__desc mb-4">
              QR codes para pagamentos e indicações. Podem ser impressos e usados para facilitar transações e
              indicações.
            </p>

            <!-- QR Codes lado a lado -->
            <v-row v-if="staticQRCodeData || indicationQRCodeData" class="mb-6">
              <!-- QR Code de Pagamento Estático -->
              <v-col cols="12" md="6" v-if="staticQRCodeData">
                <v-card elevation="0" class="settings-subcard pa-4 h-100">
                  <v-card-title class="settings-subcard__title pa-0 mb-3">
                    <v-icon left color="primary" small>mdi-credit-card</v-icon>
                    QR Code de Pagamento
                  </v-card-title>
                  <div class="text-center">
                    <img :src="staticQRCodeData" alt="QR Code de Pagamento"
                      class="settings-qr-img" />
                    <p class="caption settings-muted mt-2 mb-3">
                      Pagamentos estáticos
                    </p>
                    <v-btn color="success" dark @click="downloadQRCode('payment')" block>
                      <v-icon left>mdi-download</v-icon>
                      Baixar QR Pagamento
                    </v-btn>
                  </div>
                </v-card>
              </v-col>

              <!-- QR Code de Indicação -->
              <v-col cols="12" md="6" v-if="indicationQRCodeData">
                <v-card elevation="0" class="settings-subcard pa-4 h-100">
                  <v-card-title class="settings-subcard__title pa-0 mb-3">
                    <v-icon left color="primary" small>mdi-account-plus</v-icon>
                    QR Code de Indicação
                  </v-card-title>
                  <div class="text-center">
                    <img :src="indicationQRCodeData" alt="QR Code de Indicação"
                      class="settings-qr-img" />
                    <p class="caption settings-muted mt-2 mb-3">
                      Indicações de parceiros
                    </p>
                    <v-btn color="primary" dark @click="downloadQRCode('indication')" block>
                      <v-icon left>mdi-download</v-icon>
                      Baixar QR Indicação
                    </v-btn>
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Botão de Salvar - Desktop (Telas > 1263px) -->
    <v-row class="mt-4 d-none desktop-save">
      <v-col cols="12" class="d-flex justify-end align-center">
        <v-chip
          v-if="hasChanges"
          color="warning"
          text-color="white"
          class="mr-3"
        >
          <v-icon left small>mdi-alert-circle</v-icon>
          Alterações não salvas
        </v-chip>
        <v-btn
          v-if="hasChanges"
          color="primary"
          dark
          large
          @click="handleSaveSettings"
          :loading="saving"
        >
          <v-icon left>mdi-content-save</v-icon>
          Salvar Configurações
        </v-btn>
      </v-col>
    </v-row>

    <!-- Botão de Salvar - Mobile/Tablet (Fixed Bottom) -->
    <v-bottom-navigation
      v-if="hasChanges"
      class="mobile-save"
      fixed
      color="primary"
      height="80"
    >
      <v-btn
        color="primary"
        large
        block
        dark
        @click="handleSaveSettings"
        :loading="saving"
        class="ma-2"
      >
        <span class="save-button-content"><v-icon left color="white">mdi-content-save</v-icon> Salvar Configurações</span>
      </v-btn>
    </v-bottom-navigation>

    <!-- Indicador de Alterações - Mobile/Tablet (Floating) -->
    <v-chip
      v-if="hasChanges"
      class="mobile-save floating-alert-chip"
      color="warning"
      text-color="white"
    >
      <v-icon left small>mdi-alert-circle</v-icon>
      Alterações não salvas
    </v-chip>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { PartnerDetailsType } from "@/types/partner/Partner";
import { GetPartner } from "@/services/partners/GetPartner";
import { UpdateSettings } from "@/services/settings/UpdateSettings";
import { UpdatePartner } from "@/services/partners/UpdatePartner";
import QRCode from "qrcode";

import { buildStaticQRCodeText } from "@/utils/transactionQrcode";
import PageLoading from "@/components/loading/PageLoading.vue";

export default defineComponent({
  components: { PageLoading },
  data() {
    return {
      loading: true,
      saving: false,
      originalPartner: null as PartnerDetailsType | null,
      partner: {
        id: "",
        name: "",
        status: "PENDING",
        nickname: "",
        tax: 5,
        typeCompany: "PJ",
        category: "",
        phone: "",
        email: "",
        document: "",
        pixkey: "",
        cnaeCode: "",
        address: {
          state: "",
          street: "",
          addressNumber: "",
          neighborhood: "",
          complement: "",
          zipCode: "",
          cityName: "",
          ibge: "",
        },
        users: [],
        user: {
          name: "",
          email: "",
          phone: "",
          document: "",
          password: "",
          nickname: "",
          birthdate: "",
          motherName: "",
        },
        associate: [],
        businessHours: [],
        createdAt: "",
        digitalAccountNumber: "",
        digitalAccountDocument: "",
        associates: [],
        categoryId: "",
        contractFile: "",
        registrationStatusFile: "",
        partnerConfigs: {
          id: 0,
          onlineSale: false,
          pdvSale: false,
          updatedAt: "",
          instantCashback: 0,
          greenBuy: false,
        },
        description: "",
        instagram: "",
        whatsapp: "",
      } as PartnerDetailsType,
      categories: [],
      files: null,
      avatarPhoto: "",
      dialog: false,
      generatingQR: false,
      staticQRCodeData: "",
      staticQRString: "",
      indicationQRCodeData: "",
      indicationQRString: "",
      showGreenBuySection: false,
    };
  },
  methods: {
    changeRouter() {
      this.$router.push({ name: "myData" });
    },
    async handleGetDataPartner() {
      this.loading = true;
      const getPartner = new GetPartner();
      const partner = await getPartner.execute();

      partner.document.length > 11
        ? (this.partner.typeCompany = "PJ")
        : (this.partner.typeCompany = "PF");
      this.partner.id = partner.id || "";
      this.partner.name = partner.name || "";
      this.partner.nickName = partner.nickname || "";
      this.partner.document = partner.document || "";
      this.partner.cnaeCode = partner.cnaeCode || "";
      this.partner.category = partner.category || "";
      this.partner.categoryId = partner.categoryId || "";
      this.partner.tax = partner.tax || 0;
      this.partner.status = partner.status;
      this.partner.createdAt = partner.createdAt || "";
      this.partner.digitalAccountDocument =
        partner.digitalAccountDocument || "";
      this.partner.digitalAccountNumber = partner.digitalAccountNumber || "";
      this.partner.pixkey = partner.pixkey || "";
      this.partner.phone = partner.phone || "";
      this.partner.email = partner.email || "";
      this.partner.address.street = partner.address.street || "";
      this.partner.address.ibge = partner.address.ibge;
      this.partner.address.addressNumber = partner.address.addressNumber || "";
      this.partner.address.neighborhood = partner.address.neighborhood || "";
      this.partner.address.complement = partner.address.complement || "";
      this.partner.address.zipCode = partner.address.zipCode || "";
      this.partner.address.cityName = partner.address.cityName || "";
      this.partner.address.state = partner.address.state || "";
      this.partner.user = partner.users[0];
      this.partner.associates = partner.associates || [];
      this.partner.businessHours = partner.businessHours || [];
      this.partner.contractFile = partner.contractFile || "";
      this.partner.registrationStatusFile =
        partner.registrationStatusFile || "";
      this.partner.partnerConfigs.id = partner.partnerConfigs.id || 0;
      this.partner.partnerConfigs.onlineSale =
        partner.partnerConfigs.onlineSale || false;
      this.partner.partnerConfigs.pdvSale =
        partner.partnerConfigs.pdvSale || false;
      this.partner.partnerConfigs.updatedAt =
        partner.partnerConfigs.updatedAt || "";
      this.partner.partnerConfigs.instantCashback =
        partner.partnerConfigs.instantCashback || 0;
      this.partner.partnerConfigs.greenBuy =
        partner.partnerConfigs.greenBuy || false;
      this.partner.indicationKey = partner.indicationKey || "";
      this.showGreenBuySection = partner.id === "7e7b25d6-1d87-44eb-83db-8de2d8d9c769";

      // Salvar dados originais para comparação
      this.originalPartner = JSON.parse(JSON.stringify(this.partner));

      this.loading = false;

      // Gerar QR codes automaticamente após carregar os dados
      this.generateStaticQRCode();
      this.generateIndicationQRCode();
    },
    validateTaxAndCashback() {
      const total = this.partner.tax + this.partner.partnerConfigs.instantCashback;

      // Validação mais flexível - apenas verificar se não excede 100%
      if (total > 100) {
        this.$store.dispatch("showSnack", {
          message: "A soma da taxa administrativa e cashback direto não pode exceder 100%",
          color: "error",
        });
        return false;
      }

      return true;
    },
    async handleSaveSettings() {
      if (!this.validateTaxAndCashback()) {
        return;
      }

      this.saving = true;

      try {
        await this.handleUpdatePartner();

        // Atualizar dados originais após salvar
        this.originalPartner = JSON.parse(JSON.stringify(this.partner));

        this.$store.dispatch("showSnack", {
          message: "Configurações salvas com sucesso!",
          color: "success",
        });
      } catch (error) {
        console.error("Erro ao salvar configurações:", error);
        this.$store.dispatch("showSnack", {
          message: "Erro ao salvar configurações",
          color: "error",
        });
      } finally {
        this.saving = false;
      }
    },
    async handleUpdatePartner() {
      const updatePartnerSettings = new UpdateSettings();
      const updatePartnerData = new UpdatePartner();

      this.partner.partnerConfigs.pdvSale = true;
      this.partner.partnerConfigs.onlineSale = true;

      await updatePartnerSettings.execute(this.partner);
      await updatePartnerData.execute(this.partner);
    },
    formatPrice(value: number): string {
      if (!value && value !== 0) return "";
      const formattedValue = value.toFixed(2).replace(".", ",");
      return `R$ ${formattedValue}`;
    },
    async generateStaticQRCode() {
      if (!this.partner.document || !this.partner.name || !this.partner.id) {
        this.$store.dispatch("showSnack", {
          message: "Dados do partner incompletos para gerar QR code",
          color: "error",
        });
        return;
      }

      this.generatingQR = true;

      try {
        this.staticQRString = buildStaticQRCodeText({
          partnerId: this.partner.id,
          document: this.partner.document,
          name: this.partner.name,
          pagbank: true,
        });

        this.staticQRCodeData = await QRCode.toDataURL(this.staticQRString, {
          width: 300,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        });
      } catch (error) {
        console.error("Erro ao gerar QR code:", error);
      } finally {
        this.generatingQR = false;
      }
    },
    async generateIndicationQRCode() {
      if (!this.partner.indicationKey) {
        return;
      }

      try {
        this.indicationQRString = `https://site.brpoints.com.br/indication/?type=PARTNER&code=${this.partner.indicationKey}`;

        this.indicationQRCodeData = await QRCode.toDataURL(this.indicationQRString, {
          width: 300,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        });
      } catch (error) {
        console.error("Erro ao gerar QR code de indicação:", error);
      }
    },
    downloadQRCode(type: 'payment' | 'indication') {
      let qrCodeData = '';
      let fileName = '';
      let message = '';

      if (type === 'payment') {
        qrCodeData = this.staticQRCodeData;
        fileName = `qrcode-pagamento-${this.partner.name.replace(/\s+/g, '-').toLowerCase()}.png`;
        message = "QR Code de pagamento baixado com sucesso!";
      } else if (type === 'indication') {
        qrCodeData = this.indicationQRCodeData;
        fileName = `qrcode-indicacao-${this.partner.name.replace(/\s+/g, '-').toLowerCase()}.png`;
        message = "QR Code de indicação baixado com sucesso!";
      }

      if (!qrCodeData) {
        this.$store.dispatch("showSnack", {
          message: "Nenhum QR code para baixar",
          color: "error",
        });
        return;
      }

      try {
        // Criar um link temporário para download
        const link = document.createElement('a');
        link.href = qrCodeData;
        link.download = fileName;

        // Adicionar ao DOM, clicar e remover
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        this.$store.dispatch("showSnack", {
          message: message,
          color: "success",
        });
      } catch (error) {
        console.error("Erro ao baixar QR code:", error);
        this.$store.dispatch("showSnack", {
          message: "Erro ao baixar QR code",
          color: "error",
        });
      }
    },
  },
  mounted() {
    this.handleGetDataPartner();
  },
  computed: {
    calculatedBRP(): string {
      let value = ((1.25 / this.partner.tax) * 100)
        .toFixed(2)
        .replace(".", ",");
      return `R$ ${value}`;
    },
    hasChanges(): boolean {
      if (!this.originalPartner) return false;

      return (
        this.partner.tax !== this.originalPartner.tax ||
        this.partner.partnerConfigs.instantCashback !== this.originalPartner.partnerConfigs.instantCashback ||
        this.partner.partnerConfigs.greenBuy !== this.originalPartner.partnerConfigs.greenBuy
      );
    }
  },
});
</script>

<style scoped lang="scss">
@import "@/assets/scss/design-system/tokens";

.settings-page {
  width: 100%;
  color: $br-ink;
}

.settings-card {
  border-radius: $br-radius-lg !important;
  border: 1px solid $br-border-soft !important;
  background: $br-bg-elevated !important;
  box-shadow: $br-shadow-sm !important;
  margin-bottom: $br-space-4;
  padding: $br-space-5 !important;
}

.settings-card__title {
  font-size: 1.05rem !important;
  font-weight: 700 !important;
  color: $br-ink-soft !important;
  letter-spacing: 0.15px;
}

.settings-card__desc,
.settings-muted {
  color: $br-muted !important;
  font-size: $br-font-size-md;
  font-weight: 500;
  line-height: 1.45;
}

.settings-subcard {
  border-radius: $br-radius-md !important;
  border: 1px solid $br-border-soft !important;
  background: $br-bg-subtle !important;
  transition: box-shadow $br-transition-base, transform $br-transition-base;

  &:hover {
    box-shadow: $br-shadow-sm !important;
  }
}

.settings-subcard__title {
  font-size: $br-font-size-md !important;
  font-weight: 700 !important;
  color: $br-ink-soft !important;
}

.settings-field-label {
  font-size: $br-font-size-md;
  font-weight: 600;
  color: $br-ink-soft;
}

.settings-qr-img {
  max-width: 150px;
  max-height: 150px;
  border-radius: $br-radius-sm;
  border: 1px solid $br-border-soft;
  background: $br-bg-elevated;
  padding: $br-space-2;
}

.desktop-save {
  display: flex !important;
}

@media (max-width: 1263px) {
  .desktop-save {
    display: none !important;
  }
}

.mobile-save {
  display: none;
}

@media (max-width: 1263px) {
  .mobile-save {
    display: block !important;
  }
}

.floating-alert-chip {
  position: fixed;
  top: 80px;
  right: 16px;
  z-index: 1000;
  font-weight: 600;
  box-shadow: $br-shadow-md;
}

.save-button-content {
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
}
</style>
